import CloseIcon from "@mui/icons-material/Close";
import { Box, Dialog, DialogActions, DialogContent, Divider, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import CustomButton from "../Common/CustomButton";
import { TagsInputUniqueKeys } from "../Common/TagInputsUniqueKeys";
import useFetch from "../Common/useFetch";
import { AllowedFilterKeysUnpaidInvoices } from "./UploadInvoice";

export default function UploadInvoiceFilters({
  open,
  onClose,
  setAppliedFilters,
  appliedFilters,
}: {
  open: boolean;
  onClose: () => void;
  setAppliedFilters: React.Dispatch<React.SetStateAction<Record<AllowedFilterKeysUnpaidInvoices, string[]>>>;
  appliedFilters: Record<AllowedFilterKeysUnpaidInvoices, string[]>;
}) {
  const [vendorNames, setVendorNames] = useState<string[]>(appliedFilters.vendorName || []);
  const [PANNumbers, setPANNumbers] = useState<string[]>(appliedFilters.panNumber || []);
  const [GRNNumbers, setGRNNumbers] = useState<string[]>(appliedFilters.grnNumber || []);
  const [invoiceNumbers, setInvoiceNumbers] = useState<string[]>(appliedFilters.invoiceNumber || []);

  const [vendorNameOptions, setVendorNamesOptions] = useState<{ vendorName: string }[]>([]);
  const [PANOptions, setPANOptions] = useState<{ panNumber: string }[]>([]);
  const [GRNOptions, setGRNOptions] = useState<{ grnNumber: string }[]>([]);
  const [invoiceNumberOptions, setInvoiceNumberOptions] = useState<{ invoiceNumber: string }[]>([]);

  const getFilterOptions = async () => {
    await useFetch("/unpaid-invoices/filter/options", "GET", {
      config: {
        params: {
          anchorId: 2,
        },
      },
      failureMessage: "Failed to fetch filter options",
      thenCallBack: (response) => {
        const data = response.data.result;
        setVendorNamesOptions(
          data.vendorName.map((name: string) => {
            return { vendorName: name };
          }) || []
        );
        setPANOptions(data.panNumber.map((pan: string) => ({ panNumber: pan.trim() })) || []);
        setGRNOptions(data.grnNumber.map((grn: string) => ({ grnNumber: grn.trim() })) || []);
        setInvoiceNumberOptions(data.invoiceNumber.map((invoice: string) => ({ invoiceNumber: invoice.trim() })) || []);
      },
      errorCallback: (error) => {
        console.error("Error fetching filter options:", error);
      },
    });
  };

  useEffect(() => {
    getFilterOptions();
  }, []);

  const handleApplyFilter = () => {
    setAppliedFilters({
      vendorName: vendorNames.filter((v) => v.trim() !== ""),
      panNumber: PANNumbers.filter((v) => v.trim() !== ""),
      grnNumber: GRNNumbers.filter((v) => v.trim() !== ""),
      invoiceNumber: invoiceNumbers.filter((v) => v.trim() !== ""),
    });
    onClose();
  };

  const handleClearFilters = () => {
    setAppliedFilters({
      vendorName: [],
      panNumber: [],
      grnNumber: [],
      invoiceNumber: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} sx={{ "& .MuiDialog-paper": { borderRadius: "4px", minWidth: "600px" } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Apply Filter
        </Typography>
        <IconButton aria-label="close" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Vendor Name</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={vendorNames}
                setTags={setVendorNames}
                fullWidth={true}
                label=""
                placeholder="Newline separated Vendor Names"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={vendorNameOptions}
                accessor={"vendorName"}
                uniqueKeyAccessor={"vendorName"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">PAN</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={PANNumbers}
                setTags={setPANNumbers}
                fullWidth={true}
                label=""
                placeholder="Newline separated PAN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={PANOptions}
                accessor={"panNumber"}
                uniqueKeyAccessor={"panNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">GRN Number</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={GRNNumbers}
                setTags={setGRNNumbers}
                fullWidth={true}
                label=""
                placeholder="Newline separated GRN Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={GRNOptions}
                accessor={"grnNumber"}
                uniqueKeyAccessor={"grnNumber"}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px 16px",
            }}
          >
            <Typography variant="subtitle2">Invoice Number</Typography>
            <Box sx={{ width: "400px" }}>
              <TagsInputUniqueKeys
                tags={invoiceNumbers}
                setTags={setInvoiceNumbers}
                fullWidth={true}
                label=""
                placeholder="Newline separated Invoice Numbers"
                splitter="newline"
                limitTags={1}
                textFieldProps={{ multiline: true, maxRows: 1 }}
                options={invoiceNumberOptions}
                accessor={"invoiceNumber"}
                uniqueKeyAccessor={"invoiceNumber"}
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <CustomButton
          onClick={() => {
            handleClearFilters();
          }}
          variant="text"
        >
          Clear Filter
        </CustomButton>
        <CustomButton
          variant="contained"
          onClick={() => {
            handleApplyFilter();
          }}
        >
          Apply
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
